import { useRef, useState } from 'react';
import { useSubirImagen } from '../../hooks/useProductos';
import { useToast } from '../shared/Toast';
import { XIcon } from '../shared/icons';

const TIPOS_PERMITIDOS = ['image/jpeg', 'image/png', 'image/webp'];
const TAMANO_MAXIMO_BYTES = 5 * 1024 * 1024; // 5MB
const MAXIMO_IMAGENES = 4;

interface Props {
  imagenes: string[];
  onChange: (imagenes: string[]) => void;
}

/** BR-CAT-06: sube imagenes de producto via URL prefirmada de S3. La primera imagen del
 * array es la "principal" (misma convencion ya usada por ProductCard/ProductoDetallePage,
 * sin cambios de backend). Tope de 4 imagenes — coincide con las miniaturas que ya muestra
 * ProductoDetallePage. Quitar una imagen solo la saca del array, no borra el objeto en S3
 * (no vale la pena la complejidad de un endpoint de borrado para esto). */
export function ProductImageUploader({ imagenes, onChange }: Props) {
  const subirImagen = useSubirImagen();
  const { mostrarError } = useToast();
  const inputRef = useRef<HTMLInputElement>(null);
  const [arrastrando, setArrastrando] = useState(false);
  const [subiendo, setSubiendo] = useState(false);

  const espacioDisponible = MAXIMO_IMAGENES - imagenes.length;

  const procesarArchivos = async (archivos: FileList | File[]) => {
    const lista = Array.from(archivos);
    if (lista.length === 0) return;

    if (espacioDisponible <= 0) {
      mostrarError(`Ya tienes el máximo de ${MAXIMO_IMAGENES} imágenes. Quita alguna para agregar otra.`);
      return;
    }

    const validos: File[] = [];
    for (const archivo of lista) {
      if (!TIPOS_PERMITIDOS.includes(archivo.type)) {
        mostrarError(`"${archivo.name}" no es un formato permitido (solo JPG, PNG o WEBP).`);
        continue;
      }
      if (archivo.size > TAMANO_MAXIMO_BYTES) {
        mostrarError(`"${archivo.name}" pesa demasiado (máximo 5MB).`);
        continue;
      }
      validos.push(archivo);
    }

    if (validos.length === 0) return;

    const aSubir = validos.slice(0, espacioDisponible);
    if (validos.length > aSubir.length) {
      mostrarError(`Solo se admiten ${MAXIMO_IMAGENES} imágenes en total — se subieron ${aSubir.length}.`);
    }

    setSubiendo(true);
    try {
      const urls = await Promise.all(
        aSubir.map((archivo) => subirImagen.mutateAsync({ archivo })),
      );
      onChange([...imagenes, ...urls]);
    } catch (error) {
      mostrarError((error as Error).message ?? 'No se pudieron subir una o más imágenes.');
    } finally {
      setSubiendo(false);
    }
  };

  const hacerPrincipal = (indice: number) => {
    const copia = [...imagenes];
    const [elegida] = copia.splice(indice, 1);
    onChange([elegida!, ...copia]);
  };

  const quitar = (indice: number) => {
    onChange(imagenes.filter((_, i) => i !== indice));
  };

  return (
    <div className="flex flex-col gap-3">
      <label className="text-xs font-bold uppercase tracking-wide text-admin-ink-muted">
        Imágenes ({imagenes.length}/{MAXIMO_IMAGENES})
      </label>

      {imagenes.length > 0 && (
        <div className="flex flex-wrap gap-3">
          {imagenes.map((url, i) => (
            <div key={url} className="flex flex-col gap-1 w-20">
              <div className="relative w-20 h-20 rounded-lg overflow-hidden border border-admin-border">
                <img src={url} alt="" className="w-full h-full object-cover" />
                {i === 0 && (
                  <span className="absolute top-1 left-1 bg-terracotta text-white text-[9px] font-bold px-1.5 py-0.5 rounded">
                    Principal
                  </span>
                )}
                <button
                  type="button"
                  onClick={() => quitar(i)}
                  aria-label="Quitar imagen"
                  className="absolute top-1 right-1 bg-ink/70 text-white rounded-full w-5 h-5 flex items-center justify-center"
                >
                  <XIcon width={11} height={11} />
                </button>
              </div>
              {i !== 0 && (
                <button
                  type="button"
                  onClick={() => hacerPrincipal(i)}
                  className="text-[10px] font-semibold text-terracotta text-center"
                >
                  Hacer principal
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {espacioDisponible > 0 && (
        <div
          onClick={() => inputRef.current?.click()}
          onDragOver={(e) => {
            e.preventDefault();
            setArrastrando(true);
          }}
          onDragLeave={() => setArrastrando(false)}
          onDrop={(e) => {
            e.preventDefault();
            setArrastrando(false);
            void procesarArchivos(e.dataTransfer.files);
          }}
          data-testid="product-image-dropzone"
          className={`border-2 border-dashed rounded-lg p-5 text-center cursor-pointer transition-colors ${
            arrastrando ? 'border-terracotta bg-terracotta/5' : 'border-admin-border'
          }`}
        >
          <input
            ref={inputRef}
            type="file"
            accept={TIPOS_PERMITIDOS.join(',')}
            multiple
            hidden
            data-testid="product-image-input"
            onChange={(e) => {
              if (e.target.files) void procesarArchivos(e.target.files);
              e.target.value = '';
            }}
          />
          <p className="text-xs font-semibold text-admin-ink">
            {subiendo ? 'Subiendo…' : 'Arrastra imágenes acá o hacé click para elegir'}
          </p>
          <p className="text-[11px] text-admin-ink-muted mt-1">
            JPG, PNG o WEBP · máx. 5MB · ideal cuadradas · hasta {MAXIMO_IMAGENES} imágenes
          </p>
        </div>
      )}
    </div>
  );
}
